import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, iif, map, of, switchMap, take } from 'rxjs';
import { ModalService } from '@shared/components/modal-list/modal-list.service';
import { TOASTER_TYPE } from '@shared/components/toaster/toaster.model';
import { ToasterService } from '@shared/components/toaster/toaster.service';
import { AuthService } from '@core/authentication/services/auth.service';
import { ForgotPasswordDialogComponent } from '@b2b/features/forgot-password-dialog/forgot-password-dialog.component';
import { ForgotPasswordConnector } from '@b2b/features/forgot-password-dialog/core/forgot-password.connector';
import { ChangePasswordOptions, ChangePasswordPayload, ForgotPasswordStep } from '@b2b/features/forgot-password-dialog/core/forgot-password.types';

@Injectable({
  providedIn: 'root',
})
export class ForgotPasswordService {
  #currentStep = new BehaviorSubject<ForgotPasswordStep>('RECEIVE_CODE');
  #isLoading = new BehaviorSubject(false);
  #message = new BehaviorSubject('');
  #error = new BehaviorSubject('');
  #username = new BehaviorSubject('');
  #otp = new BehaviorSubject('');
  #newPasswordTitle = new BehaviorSubject('');

  constructor(
    private forgotPasswordConnector: ForgotPasswordConnector,
    private authService: AuthService,
    private modalService: ModalService<ChangePasswordOptions>,
    private toasterService: ToasterService,
    private translateService: TranslateService,
  ) {}

  get currentStep$() {
    return this.#currentStep.asObservable();
  }

  get isLoading$() {
    return this.#isLoading.asObservable();
  }

  get message$() {
    return this.#message.asObservable();
  }

  get error$() {
    return this.#error.asObservable();
  }

  get newPasswordTitle$() {
    return this.#newPasswordTitle
      .asObservable()
      .pipe(
        switchMap(() =>
          iif(
            () => this.authService.isAuthenticated(),
            this.translateService.get('forgotPassword.changePassword.title'),
            this.translateService.get('forgotPassword.title'),
          ),
        ),
      );
  }

  openModal(data?: ChangePasswordOptions) {
    this.#currentStep.next(this.authService.isAuthenticated() ? 'NEW_PASSWORD' : 'RECEIVE_CODE');
    this.modalService.addModal({
      component: ForgotPasswordDialogComponent,
      id: 'forgot-pw-modal',
      title: '',
      classList: 'forgot-pw-modal',
      hasCloseButton: true,
      disableClose: true,
      data,
    });
  }

  reset() {
    this.#error.next('');
  }

  receiveCode(username: string, resend = false) {
    this.#isLoading.next(true);
    this.forgotPasswordConnector
      .receiveCode(username)
      .pipe(
        switchMap(({ message }) =>
          iif(
            () => resend,
            this.translateService
              .get('forgotPassword.confirmCode.resendCodeSuccess')
              .pipe(map(translated => ({ message: translated }))),
            of({ message }),
          ),
        ),
        take(1),
      )
      .subscribe({
        next: ({ message }) => {
          this.#isLoading.next(false);
          this.#error.next('');
          this.#username.next(username);
          this.#currentStep.next('CONFIRM_CODE');
          if (resend) {
            this.toasterService.init({
              id: 'resend-code-success',
              type: TOASTER_TYPE.SUCCESS,
              content: message,
            });
          } else {
            this.#message.next(message);
          }
        },
        error: ({ error }) => {
          this.#isLoading.next(false);
          this.#error.next(error.message);
        },
      });
  }

  confirmCode(otp: string) {
    this.#isLoading.next(true);
    this.forgotPasswordConnector
      .confirmCode(this.#username.value, otp)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.#isLoading.next(false);
          this.#error.next('');
          this.#otp.next(otp);
          this.#currentStep.next('NEW_PASSWORD');
        },
        error: ({ error }) => {
          this.#isLoading.next(false);
          this.#error.next(error.message);
        },
      });
  }

  resendCode() {
    this.receiveCode(this.#username.value, true);
  }

  sendNewPassword(newPassword: string, oldPassword?: string) {
    const data = this.getChangePasswordPayload(newPassword, oldPassword);
    this.#isLoading.next(true);
    this.forgotPasswordConnector
      .sendNewPassword(data)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.#isLoading.next(false);
          this.#currentStep.next('SUCCESS');
        },
        error: ({ error }) => {
          this.#isLoading.next(false);
          this.#error.next(error.message);
        },
      });
  }

  getChangePasswordPayload(newPassword: string, oldPassword?: string): ChangePasswordPayload {
    const payload: ChangePasswordPayload = { newPassword };
    if (this.authService.isAuthenticated()) {
      if (!oldPassword) {
        throw new Error('oldPassword is required');
      }
      const refresh_token = this.authService.getRefreshToken()!;
      payload.oldPassword = oldPassword;
      payload.refreshToken = refresh_token;
    } else {
      if (!this.#username.value) {
        throw new Error('username is required');
      }
      if (!this.#otp.value) {
        throw new Error('otp is required');
      }
      payload.username = this.#username.value;
      payload.otp = this.#otp.value;
    }
    return payload;
  }
}
